import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { useAuth } from '@/app/auth';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { SearchInput } from '@/components/ui/SearchInput';
import { Select } from '@/components/ui/Select';
import { SortArrow } from '@/components/ui/SortArrow';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import type { Locale } from '@/i18n/types';
import { cx } from '@/lib/cx';
import { airportCity, airportCountry, airportName } from '@/types/airport';
import type { Airport, AirportSortField } from '@/types/airport';
import { ROLES_DICTIONARY_WRITE } from '@/types/user';
import { AirportPanel } from './AirportPanel';
import { ROWS_OPTIONS, useAirportParams } from './useAirportParams';
import { useAirportList } from './useAirportList';
import styles from './AirportsPage.module.css';

/**
 * The airport dictionary.
 *
 * Readable by `ADMIN`, `AAK`, `USER` and `KGA_USER`; writable by `ADMIN` alone, so
 * the add button and the panel's actions are gated on the role while the table and
 * the card are not. That gating is a courtesy — the server refuses a write from any
 * other role with a 403 regardless — but a control that can only answer 403 wastes
 * the reader's time.
 *
 * **Every filter is a server query.** Unlike the user list, this is 9 400 rows behind
 * `GET /airport/list`, which filters, sorts and pages them; the URL holds the request
 * and the response is displayed as it arrives.
 */
export const AirportsPage = () => {
  const { params, criteria, update, select, reset, hasFilters } = useAirportParams();
  const { rows, currPage, total, isLastPage, isLoading, errorKey, reload } =
    useAirportList(criteria);
  const { can } = useAuth();
  const { t, locale } = useTranslation();

  /**
   * Always true today: the route is ADMIN-only, and ADMIN is the write role.
   *
   * Kept because the page should not assume the guard in front of it — the read-only
   * card is what a non-administrator would correctly get if this screen is ever
   * opened to the roles the server already allows to read the dictionary.
   */
  const canWrite = can(ROLES_DICTIONARY_WRITE);

  /**
   * The airport the panel is showing, held outside the table.
   *
   * The panel cannot be fed from the page in view alone. A newly created airport is
   * very often not on it — sorted by code, a new `ZZZ` belongs on the last page — and
   * an open card would otherwise slam shut the moment the reader pages past its row,
   * which is not what "next page" asks for.
   *
   * It is written where the airport is already in hand: the row that was clicked, and
   * the object a write returned. No effect copies it, and the table's own copy wins
   * whenever it has one, because a reload is the more recent truth.
   */
  const [held, setHeld] = useState<Airport | null>(null);

  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    if (flash === null) return undefined;

    const timer = window.setTimeout(() => setFlash(null), 6000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  // Read out once: narrowing a union through `params.selection` is lost the moment it
  // crosses into a callback.
  const { selection } = params;

  const selected =
    selection.mode === 'edit'
      ? (rows.find((row) => row.id === selection.id) ??
        (held?.id === selection.id ? held : null))
      : null;

  // Neither on the page nor in hand: a link to an airport that has been deleted, or
  // whose id never existed. Anything the reader opened themselves is held above, so
  // this cannot fire for a row they merely paged away from.
  const missing = selection.mode === 'edit' && selected === null && !isLoading && !errorKey;

  // Creating and editing open the same dialog; only editing has a row to show.
  const isCreating = selection.mode === 'create';

  const open = (airport: Airport) => {
    setHeld(airport);
    select({ mode: 'edit', id: airport.id });
  };

  const handleSaved = (airport: Airport, action: 'created' | 'saved') => {
    setHeld(airport);
    setFlash(t(`airports.flash.${action}`, { code: airport.airportIataCode ?? String(airport.id) }));

    // The list is sorted and paged by the SERVER, so a saved row cannot be folded
    // into the page in hand — a new code, or an edited one, belongs wherever the
    // order puts it. Re-reading the page is both simpler and correct.
    reload();

    // The dialog closes, and the message above the table is what confirms the save —
    // inside the dialog it would sit behind the scrim.
    select({ mode: 'closed' });
  };

  const handleDeleted = (airport: Airport) => {
    setHeld(null);
    setFlash(
      t('airports.flash.deleted', { code: airport.airportIataCode ?? String(airport.id) }),
    );
    select({ mode: 'closed' });
    reload();
  };

  return (
    <>
      <div className={styles.page}>
        <div className={styles.toolbar}>
          <h1 className={styles.title}>{t('airports.title')}</h1>

          <div className={styles.spacer} />

          <Button size="sm" onClick={reload} disabled={isLoading}>
            {t('common.refresh')}
          </Button>
          {canWrite && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => select({ mode: 'create' })}
              disabled={selection.mode === 'create'}
            >
              {t('airports.add')}
            </Button>
          )}
        </div>

        <div className={styles.filters}>
          {/* One box for "search anything" — code, ICAO, airport, city or country, in
              any of the three languages. The narrower fields beside it are for when
              that is too broad. */}
          <SearchInput
            label={t('common.search')}
            placeholder={t('airports.searchPlaceholder')}
            value={params.query}
            onChange={(query) => update({ query })}
            width="lg"
          />

          <SearchInput
            label={t('airports.columns.country')}
            placeholder={t('airports.countryPlaceholder')}
            value={params.country}
            onChange={(country) => update({ country })}
          />

          <SearchInput
            label={t('airports.columns.city')}
            value={params.city}
            onChange={(city) => update({ city })}
          />

          {/* The WHOLE code — this parameter does not match partially. Partial code
              matching is what the search box above does. */}
          <SearchInput
            label={t('airports.columns.iata')}
            value={params.iataCode}
            onChange={(iataCode) => update({ iataCode })}
            upper
            width="sm"
          />

          <label className={styles.filter}>
            <span className={styles.filterLabel}>{t('airports.columns.status')}</span>
            <Select
              options={[
                { value: 'true', label: t('airports.states.active') },
                { value: 'false', label: t('airports.states.inactive') },
              ]}
              placeholder={t('airports.allStates')}
              value={params.active}
              active={params.active !== ''}
              onChange={(event) =>
                update({ active: event.target.value as typeof params.active })
              }
              aria-label={t('airports.columns.status')}
            />
          </label>

          <Button size="sm" onClick={reset} disabled={!hasFilters}>
            {t('airports.resetFilters')}
          </Button>
        </div>

        {flash && (
          <p className={styles.flash} role="status">
            {flash}
          </p>
        )}

        {errorKey && (
          <p className={styles.error} role="alert">
            {t(errorKey)}
          </p>
        )}

        {missing && <p className={styles.warning}>{t('airports.notFound')}</p>}

        <div className={styles.tableWrap}>
          {isLoading && <div className={styles.loadingBar} aria-hidden="true" />}

          <table className={cx(styles.table, isLoading && styles.loading)}>
            <thead>
              <tr className={styles.headRow}>
                {COLUMNS.map((column) => (
                  <SortHead
                    key={column.labelKey}
                    column={column}
                    locale={locale}
                    sort={params.sort}
                    dir={params.dir}
                    onSort={(field) =>
                      update({
                        sort: field,
                        dir:
                          params.sort === field && params.dir === 'ascending'
                            ? 'descending'
                            : 'ascending',
                      })
                    }
                  />
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={styles.message}>
                    {t('airports.nothingFound')}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <AirportRow
                    key={row.id}
                    airport={row}
                    locale={locale}
                    isOpen={selected?.id === row.id}
                    onOpen={() => open(row)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          // The page the server RETURNED. Asking this API for a page past the end
          // answers with the last one, so showing the requested number would put the
          // last page's rows under a control that says 12. Nothing is written back to
          // the URL for it: the URL is the request, this is the result.
          page={currPage}
          rows={params.rows}
          total={total}
          countLabel={t('airports.found')}
          isLastPage={isLastPage}
          rowsOptions={ROWS_OPTIONS}
          onPage={(page) => update({ page })}
          onRows={(rowCount) => update({ rows: rowCount })}
          isLoading={isLoading}
        />
      </div>

      {/* Outside the page, because it is a dialog in the browser's top layer: the
          table never has to make room for it. */}
      {(isCreating || selected !== null) && (
        <AirportPanel
          // Keyed by the row, so choosing another one rebuilds the form from that row
          // rather than an effect copying it over what is on screen.
          key={selected === null ? 'create' : String(selected.id)}
          airport={selected}
          canWrite={canWrite}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
          onClose={() => select({ mode: 'closed' })}
        />
      )}
    </>
  );
};

/**
 * A column, and how the server is asked to sort by it.
 *
 * `sort` is a function of the LANGUAGE, not a constant: the name, city and country
 * columns each show the reader's own language, and the dictionary sorts those as
 * three separate fields. Ordering the English column by `nameRu` would look like no
 * order at all. `null` marks a column with no sort of its own.
 */
interface Column {
  labelKey: Key;
  sort: ((locale: Locale) => AirportSortField) | null;
}

const byLocale = (
  ru: AirportSortField,
  en: AirportSortField,
  kz: AirportSortField,
): ((locale: Locale) => AirportSortField) =>
  (locale) => (locale === 'ru' ? ru : locale === 'kk' ? kz : en);

const COLUMNS: readonly Column[] = [
  { labelKey: 'airports.columns.iata', sort: () => 'iataCode' },
  { labelKey: 'airports.columns.name', sort: byLocale('nameRu', 'nameEn', 'nameKz') },
  {
    labelKey: 'airports.columns.city',
    sort: byLocale('cityNameRu', 'cityNameEn', 'cityNameKz'),
  },
  {
    labelKey: 'airports.columns.country',
    sort: byLocale('countryNameRu', 'countryNameEn', 'countryNameKz'),
  },
  // Neither sorts: a zone is not something a dictionary is read in the order of, and
  // sorting two values into two blocks is what the status filter already does.
  { labelKey: 'airports.columns.timeZone', sort: null },
  { labelKey: 'airports.columns.status', sort: null },
  { labelKey: 'airports.columns.updated', sort: () => 'updated' },
];

/**
 * A sortable column head.
 *
 * At module level, not inside AirportsPage: a component created during a render is a
 * new type every time, so React unmounts and remounts its whole subtree — which in a
 * table means the filter field above loses focus mid-word.
 */
const SortHead = ({
  column,
  locale,
  sort,
  dir,
  onSort,
}: {
  column: Column;
  locale: Locale;
  sort: AirportSortField;
  dir: 'ascending' | 'descending';
  onSort: (field: AirportSortField) => void;
}) => {
  const { t } = useTranslation();

  if (column.sort === null) return <th>{t(column.labelKey)}</th>;

  const field = column.sort(locale);

  // Marked as sorted for ANY of its language variants, not just this locale's: a
  // reader who sorts by name and then switches language should still see which column
  // the order came from, rather than an arrow that vanished.
  const sorted = SORT_GROUP[column.labelKey]?.has(sort) ?? sort === field;

  return (
    <th aria-sort={sorted ? dir : 'none'}>
      <button
        type="button"
        className={cx(styles.sortButton, sorted && styles.sorted)}
        onClick={() => onSort(field)}
        title={t('airports.sortHint')}
      >
        {t(column.labelKey)}
        <SortArrow
          sorted={sorted}
          dir={dir}
          className={styles.sortArrow}
          idleClassName={styles.sortIdle}
        />
      </button>
    </th>
  );
};

/** Every sort field a language-dependent column can be ordered by. */
const SORT_GROUP: Partial<Record<Key, ReadonlySet<AirportSortField>>> = {
  'airports.columns.name': new Set<AirportSortField>(['nameRu', 'nameEn', 'nameKz']),
  'airports.columns.city': new Set<AirportSortField>([
    'cityNameRu',
    'cityNameEn',
    'cityNameKz',
  ]),
  'airports.columns.country': new Set<AirportSortField>([
    'countryNameRu',
    'countryNameEn',
    'countryNameKz',
    'countryCode',
  ]),
};

/** One airport. */
const AirportRow = ({
  airport,
  locale,
  isOpen,
  onOpen,
}: {
  airport: Airport;
  locale: Locale;
  isOpen: boolean;
  onOpen: () => void;
}) => {
  const { t, formatDate } = useTranslation();

  const icao = typeof airport.icaoCode === 'string' ? airport.icaoCode : '';

  /**
   * The whole row opens the card — a row is what the reader is aiming at, and a
   * three-character code is a small target to have to hit.
   *
   * The click sits HERE and not on the code button, which keeps its `type` and its
   * focus ring but no handler of its own: a click on it — or Enter while it has
   * focus — dispatches a click that bubbles to this handler, so one handler serves
   * both the mouse anywhere on the row and the keyboard. Two handlers would run
   * twice for the one gesture.
   *
   * The row itself gets no `tabIndex`: the button is already the row's tab stop and
   * its accessible name, and making the row focusable too would mean two stops per
   * row and a hundred on a full page.
   */
  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    // A drag across a cell ends in a click, so selecting an airport name to copy it
    // would otherwise open the card every time. `detail` is the click count, and it
    // is 0 for a click the keyboard synthesized — which cannot have come from a
    // drag, and must not be swallowed because some text is selected elsewhere.
    if (event.detail > 0 && (window.getSelection()?.toString() ?? '') !== '') return;

    onOpen();
  };

  return (
    <tr
      className={cx(
        styles.row,
        isOpen && styles.selected,
        !airport.active && styles.inactiveRow,
      )}
      onClick={handleRowClick}
    >
      <td>
        {/* A button, not a link: the card opens in a dialog over the table rather
            than navigating away, so closing it leaves the reader on the same page of
            rows. */}
        <button type="button" className={styles.codeButton} aria-haspopup="dialog">
          {airport.airportIataCode ?? '—'}
        </button>
        {/* The ICAO code under the IATA one: it has no column of its own, but it is
            what a flight plan is filed with and it is worth being able to see. */}
        {icao !== '' && <span className={styles.icao}>{icao}</span>}
      </td>

      <td className={styles.wrap}>{airportName(airport, locale) || <Empty />}</td>
      <td className={styles.wrap}>{airportCity(airport, locale) || <Empty />}</td>

      <td className={styles.wrap}>
        {airport.countryCode && <span className={styles.countryCode}>{airport.countryCode}</span>}
        {airportCountry(airport, locale) || (airport.countryCode ? null : <Empty />)}
      </td>

      <td className={styles.zone}>{airport.localTimeZone ?? <Empty />}</td>

      <td>
        <Badge tone={airport.active ? 'success' : 'neutral'}>
          {t(airport.active ? 'airports.states.active' : 'airports.states.inactive')}
        </Badge>
      </td>

      <td className={styles.date}>{airport.updated ? formatDate(airport.updated) : <Empty />}</td>
    </tr>
  );
};

const Empty = () => <span className={styles.empty}>—</span>;
